import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { X, Volume2, Loader2, ArrowRight, Check, Sparkles, BookOpen, Clock, MessageSquare } from "lucide-react";
import client from "@/lib/api";
import { useTts } from "@/hooks/useTts";
import { useLang } from "@/context/LangContext";

// Premium MELLETIC Story renderer: cinematic scene -> dialogue (tap-to-play, no autoplay)
// -> a decision moment + comprehension -> rich completion. Resumes where you left off.
export default function Story() {
  const { id } = useParams();
  const nav = useNavigate();
  const { play, stop, playing, loading, error } = useTts();
  const { t } = useLang();
  const [story, setStory] = useState(null);
  const [i, setI] = useState(0);
  const [phase, setPhase] = useState("scenes"); // scenes | q | done
  const [qi, setQi] = useState(0);
  const [sel, setSel] = useState(null);
  const [checked, setChecked] = useState(false);
  const [correct, setCorrect] = useState(0);
  const startRef = useRef(Date.now());
  const resumeKey = `mel_story_${id}`;

  useEffect(() => {
    client.get(`/learn/story/${id}`).then(({ data }) => {
      setStory(data);
      const saved = parseInt(localStorage.getItem(resumeKey) || "0", 10);
      if (saved > 0 && saved < (data.scenes?.length || 0)) setI(saved);
    }).catch(() => nav("/stories"));
  }, [id, nav]); // eslint-disable-line

  useEffect(() => { stop(); }, [i, qi, phase, stop]); // stop audio on any change (no bleed/autoplay)

  if (!story) return <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center"><Loader2 className="animate-spin text-[#2563EB]" /></div>;

  const scenes = story.scenes || [];
  const questions = story.questions || [];

  const finishStory = async () => {
    try { await client.post(`/learn/story/${id}/complete`); } catch {}
    localStorage.removeItem(resumeKey);
    window.dispatchEvent(new Event("mel:progress-refresh"));
    setPhase("done");
  };

  // ===== COMPLETE =====
  if (phase === "done") {
    const mins = Math.max(1, Math.round((Date.now() - startRef.current) / 60000));
    const phrases = scenes.filter((s) => s.target).slice(0, 4);
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
        <div className="relative h-52">
          {(story.cover || scenes[0]?.image) && <img src={story.cover || scenes[0]?.image} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-b from-[#2563EB]/25 to-[#F8FAFC]" />
          <div className="absolute -bottom-9 left-1/2 -translate-x-1/2 w-[74px] h-[74px] rounded-full bg-gradient-to-br from-[#2563EB] to-[#3B82F6] flex items-center justify-center shadow-lg ring-4 ring-white"><Check className="text-white" size={36} /></div>
        </div>
        <div className="px-6 pt-12 pb-8 overflow-y-auto">
          <h1 className="font-heading text-2xl font-extrabold text-[#0F172A] text-center">{t("story_complete")}</h1>
          <p className="text-[#475569] text-center mt-1">{story.title}</p>

          <div className="mt-5 grid grid-cols-3 gap-3">
            <Metric icon={Clock} tint="#8B5CF6" val={`${mins}m`} label={t("common_time")} />
            <Metric icon={BookOpen} tint="#22C55E" val={phrases.length} label={t("story_phrases")} />
            <Metric icon={Sparkles} tint="#2563EB" val={questions.length ? `${correct}/${questions.length}` : "—"} label={t("story_quiz")} />
          </div>

          <Section title={t("story_what_happened")}>
            <p className="text-[#334155] text-sm leading-relaxed">{story.description || t("story_desc_fallback")}</p>
          </Section>

          {phrases.length > 0 && (
            <Section title={t("story_phrases_practiced")}>
              <div className="space-y-2">
                {phrases.map((p, k) => (
                  <div key={k} className="flex items-start gap-3 bg-white rounded-2xl p-3 border border-slate-100">
                    <button onClick={() => play(p.audio_text || p.target, story.language, `ph-${k}`)} data-testid={`done-phrase-audio-${k}`}
                      className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${playing === `ph-${k}` ? "bg-[#2563EB] text-white" : "bg-[#2563EB]/10 text-[#2563EB]"}`}>
                      {loading === `ph-${k}` ? <Loader2 size={16} className="animate-spin" /> : <Volume2 size={17} />}
                    </button>
                    <div><p className="font-bold text-[#0F172A] text-sm">{p.target}</p><p className="text-[#64748B] text-xs">{p.native}</p></div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          <button onClick={() => nav("/stories")} data-testid="story-done" className="w-full mt-6 bg-[#2563EB] text-white font-bold py-4 rounded-2xl">{t("story_back")}</button>
        </div>
      </div>
    );
  }

  // ===== COMPREHENSION / DECISION =====
  if (phase === "q") {
    const q = questions[qi];
    const ok = sel === q.answer;
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col px-5">
        <div className="pt-11 flex items-center gap-3">
          <button onClick={() => nav("/stories")} data-testid="story-close-q" className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center"><X size={20} className="text-[#0F172A]" /></button>
          <div className="flex-1 flex gap-1.5">{questions.map((_, k) => <div key={k} className={`h-1.5 flex-1 rounded-full ${k <= qi ? "bg-[#2563EB]" : "bg-slate-200"}`} />)}</div>
        </div>
        <div className="flex-1 flex flex-col justify-center">
          <span className="inline-flex items-center gap-1.5 self-start text-[11px] font-extrabold uppercase tracking-widest text-[#2563EB] bg-[#E0F2FE] rounded-full px-3 py-1"><MessageSquare size={13} /> {t("story_your_turn")} · {qi + 1}/{questions.length}</span>
          <h2 className="font-heading text-2xl font-extrabold text-[#0F172A] mt-3">{q.prompt}</h2>
          <div className="mt-5 space-y-3">
            {q.options.map((o, idx) => {
              let cls = "border-slate-200 bg-white text-[#0F172A]";
              if (checked && idx === q.answer) cls = "border-emerald-400 bg-emerald-50 text-[#0F172A]";
              else if (checked && idx === sel) cls = "border-rose-400 bg-rose-50 text-[#0F172A]";
              else if (sel === idx) cls = "border-[#2563EB] bg-[#EAF1FE] text-[#0F172A]";
              return <button key={idx} disabled={checked} onClick={() => setSel(idx)} data-testid={`story-opt-${idx}`} className={`w-full text-left rounded-2xl px-4 py-4 border-2 font-semibold transition-colors flex items-center justify-between ${cls}`}>{o}{checked && idx === q.answer && <Check size={18} className="text-emerald-500" />}</button>;
            })}
          </div>
          {checked && !ok && <p className="mt-3 text-sm font-semibold text-rose-600">{t("story_not_quite", { answer: q.options[q.answer] })}</p>}
        </div>
        <div className="pb-8">
          {!checked ? (
            <button onClick={() => { setChecked(true); if (ok) setCorrect((c) => c + 1); }} disabled={sel === null} data-testid="story-check" className="w-full bg-[#2563EB] text-white font-bold py-4 rounded-2xl disabled:opacity-50">{t("common_check")}</button>
          ) : (
            <button onClick={() => { if (qi + 1 >= questions.length) finishStory(); else { setQi(qi + 1); setSel(null); setChecked(false); } }} data-testid="story-next-q" className="w-full bg-[#2563EB] text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2">{qi + 1 >= questions.length ? t("story_finish") : t("common_continue")} <ArrowRight size={18} /></button>
          )}
        </div>
      </div>
    );
  }

  // ===== SCENES =====
  const s = scenes[i];
  const goNext = () => {
    if (i + 1 >= scenes.length) { if (questions.length) setPhase("q"); else finishStory(); }
    else { const n = i + 1; setI(n); localStorage.setItem(resumeKey, String(n)); }
  };
  const key = s.audio_text || s.target;
  return (
    <div className="min-h-screen w-full bg-[#0B1220] flex justify-center">
      <div className="relative w-full max-w-[460px] min-h-screen flex flex-col bg-[#F8FAFC]">
        <div className="relative flex-1">
          <img src={s.image || story.cover} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,rgba(11,18,32,0.30) 0%,transparent 22%,transparent 46%,rgba(248,250,252,0.65) 74%,#F8FAFC 100%)" }} />
          <div className="relative z-10 flex items-center gap-3 px-4" style={{ paddingTop: "calc(env(safe-area-inset-top,0px) + 16px)" }}>
            <button onClick={() => nav("/stories")} data-testid="story-close" className="w-10 h-10 rounded-full bg-black/35 backdrop-blur text-white flex items-center justify-center"><X size={20} /></button>
            <div className="flex-1 flex gap-1.5">{scenes.map((_, k) => <div key={k} className={`h-1.5 flex-1 rounded-full ${k <= i ? "bg-white" : "bg-white/40"}`} />)}</div>
          </div>
        </div>
        <div className="px-5 pb-8 -mt-24 relative z-10">
          {story.series && <p className="text-center text-[11px] font-bold uppercase tracking-widest text-[#2563EB] mb-2">{story.series} · {t("story_scene", { n: i + 1 })}</p>}
          <div className="bg-white rounded-[26px] p-5 shadow-[0_24px_50px_-24px_rgba(15,23,42,0.5)] border border-slate-100">
            {s.speaker && <p className="text-[11px] font-extrabold uppercase tracking-widest text-[#2563EB]">{s.speaker}</p>}
            <div className="flex items-start gap-3 mt-2">
              <button onClick={() => play(key, story.language, "scene")} data-testid="story-audio"
                className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${playing === "scene" ? "bg-[#2563EB] text-white animate-pulse" : error === "scene" ? "bg-rose-100 text-rose-600" : "bg-[#2563EB]/10 text-[#2563EB]"}`}>
                {loading === "scene" ? <Loader2 size={19} className="animate-spin" /> : <Volume2 size={20} />}
              </button>
              <div><p className="font-bold text-[#0F172A] text-lg leading-snug">{s.target}</p><p className="text-[#64748B] text-sm mt-0.5">{s.native}</p></div>
            </div>
          </div>
          <button onClick={goNext} data-testid="story-next" className="w-full mt-4 bg-[#2563EB] text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2 shadow-[0_16px_32px_-14px_rgba(37,99,235,0.8)] active:scale-[0.99] transition-transform">
            {i + 1 >= scenes.length ? (questions.length ? t("story_your_turn") : t("common_finish")) : t("common_next")} <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

const Metric = ({ icon: Icon, tint, val, label }) => (
  <div className="rounded-2xl bg-white border border-slate-200/70 p-3 text-center">
    <Icon size={17} className="mx-auto" style={{ color: tint }} />
    <p className="font-heading text-lg font-extrabold text-[#0F172A] mt-1 leading-none">{val}</p>
    <p className="text-[11px] text-[#64748B] mt-0.5">{label}</p>
  </div>
);
const Section = ({ title, children }) => (
  <div className="mt-5">
    <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-2">{title}</p>
    {children}
  </div>
);
